import { palette, petrol, violet, darkSurface, feedback, alpha } from './brand';

/**
 * Tema escuro — paleta Hexium. Todos os valores vêm de `brand.ts`; nenhum hex solto aqui.
 *
 * Regra que governa este arquivo: **cor de marca escura não vira texto sobre fundo escuro.**
 * `petrol` e `violet` aparecem como PREENCHIMENTO (com texto branco por cima, 10.45:1 e 8.15:1),
 * e como TEXTO apenas via tint (`primaryAccent`, `accentText`), que passam AA sobre #111111.
 *
 * Hierarquia de superfície, do fundo para a frente:
 *   sidebar/backgroundAlt #0B0B0B → background #111111 → card #1D1D1D → tooltip/input #262626
 */
export const darkTheme = {
    // Marca — preenchimento
    primary: palette.petrol,          // fundo de botão/gradiente; texto branco por cima = 10.45:1
    primaryHover: petrol.tint15,
    primaryActive: petrol.shade15,
    /** Acento legível SOBRE superfície escura (6.19:1). Use para texto, ícone e borda ativa. */
    primaryAccent: petrol.tint45,

    // Marca — secundária (violet): acentos, CTA discreto, destaque de gráfico
    accent: palette.violet,           // fundo; texto branco por cima = 8.15:1
    accentHover: violet.tint15,
    /** Violet legível SOBRE superfície escura (6.85:1). */
    accentText: violet.tint45,

    // Superfícies
    secondary: darkSurface.raised,
    background: darkSurface.base,
    backgroundAlt: darkSurface.sunken,
    backgroundSecondary: darkSurface.raised,
    card: darkSurface.raised,
    cardBorder: darkSurface.border,
    border: darkSurface.border,
    borderLight: darkSurface.borderStrong,

    // Texto
    text: palette.white,                    // 18.88:1
    textSecondary: palette.grayLight,       // 13.95:1
    title: palette.white,
    label: palette.grayLight,
    placeholderColor: palette.grayMedium,   // 4.22:1 — suficiente para placeholder (não-essencial)
    detailLabelColor: palette.grayLight,
    detailValueColor: palette.white,
    textSubtitleCompliance: palette.grayLight,
    ruleDetailsTitleColor: palette.white,
    arrowBack: palette.white,
    inputAddEditUser: palette.white,
    inputAddEditOrganization: palette.white,
    inputAddEditRule: palette.white,
    buttonCobuttonModelorProfile: palette.white,

    // Sidebar
    sidebar: darkSurface.sunken,
    textSidebar: palette.grayLight,
    profile: darkSurface.raised,
    sidebarItem: 'transparent',
    sidebarItemHover: alpha(palette.petrol, 0.16),
    sidebarItemActive: alpha(palette.petrol, 0.26),

    // Elementos de interface
    buttonMode: palette.petrol,
    tooltip: darkSurface.overlay,
    tooltipBorder: darkSurface.border,

    // Modais
    modalBackground: alpha(palette.black, 0.85),
    modalBorder: darkSurface.border,
    deleteModalBackground: darkSurface.raised,
    deleteModalBorder: darkSurface.border,
    backgroundCancelButtonDeleteModal: 'transparent',
    borderCancelButtonDeleteModal: darkSurface.borderStrong,
    hoverCancelButtonDeleteModal: darkSurface.overlay,
    backgroundConfirmButtonDeleteModal: feedback.errorLight,
    borderConfirmButtonDeleteModal: feedback.errorLight,
    hoverConfirmButtonDeleteModal: '#A32020',

    // Formulários e entradas
    inputLogin: darkSurface.raised,
    inputBorder: darkSurface.borderStrong,
    usersDivBackground: darkSurface.raised,
    formUserBackground: darkSurface.raised,
    formUserInput: darkSurface.overlay,
    formUserBorder: darkSurface.borderStrong,
    inputBackgroundAccount: darkSurface.raised,
    inputDisabled: darkSurface.overlay,
    disabledBackground: darkSurface.overlay,
    borderInputCompliance: darkSurface.borderStrong,
    ruleDetailsInputBackground: darkSurface.overlay,
    ruleDetailsInputBorder: darkSurface.borderStrong,
    ruleDetailsInputText: palette.white,
    forgotPasswordBoxBackground: darkSurface.raised,

    // Upload
    dropzoneBackground: darkSurface.raised,
    dropzoneActiveBackground: alpha(palette.petrol, 0.18),

    // Alertas e estados
    alertBgColor: darkSurface.raised,
    alertBorderColor: darkSurface.borderStrong,
    alertIconColor: feedback.errorDark,
    alertTextColor: palette.white,
    inputErrorBg: alpha(feedback.errorDark, 0.12),
    inputErrorBorder: feedback.errorDark,
    inputSuccessBg: alpha(feedback.successDark, 0.12),
    error: feedback.errorDark,              // 8.9:1 sobre #111111
    errorBackground: alpha(feedback.errorDark, 0.12),
    success: feedback.successDark,
    successBackground: alpha(feedback.successDark, 0.12),

    // Resultados e botões
    resultBackground: darkSurface.raised,
    buttonBackground: darkSurface.overlay,
    buttonText: palette.white,
    buttonHoverBackground: '#323232',
    buttonColorProfile: palette.petrol,
    buttonBackAccount: palette.white,
    saveButtonBackground: palette.petrol,
    saveButtonText: palette.white,
    saveButtonHover: petrol.tint15,

    // Código
    codeBackground: darkSurface.sunken,
    codeText: palette.grayLight,

    /** Cor de identidade enviada ao webwidget. */
    widgetBackground: palette.petrol,


    /*
      PAPÉIS — nomeados pela FUNÇÃO, não pelo elemento. Ver a nota equivalente em `light.ts`.
    */
    /** Base do degradê de fundo; o topo é `background` (#111111). Branco sobre ela: 16.86:1. */
    backgroundDeep: darkSurface.raised,
    /** Tinta sobre `primary` (petrol, que é escura nos dois temas): branco. */
    primaryOn: palette.white,
    /**
     * Tinta sobre `error`/`success`. No tema escuro os semânticos são CLAROS (#F28B82, #6EDBAE),
     * então a tinta inverte para escura — branco sobre eles reprovaria contraste.
     */
    errorOn: palette.black,
    successOn: palette.black,

    /** Aviso — no escuro o ambar clareia para ficar legivel sobre #111111. */
    warning: '#F2B84B',
    warningBackground: '#3A2A08',
    warningOn: palette.black,

    input: {
        background: darkSurface.raised,
    },
} as const;
